import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "../lib/stripe";
import { BookingProvider, useBooking } from "../lib/BookingContext";
import { brandName } from "../lib/site";
import SiteHeader from "../components/SiteHeader";
import Stepper from "../components/Stepper";
import StepServiceLocation from "../components/wizard/StepServiceLocation";
import StepTime from "../components/wizard/StepTime";
import StepDetails from "../components/wizard/StepDetails";
import StepPayment from "../components/wizard/StepPayment";
import StepConfirmFree from "../components/wizard/StepConfirmFree";

function WizardBody() {
  const { step, service } = useBooking();
  const isPaid = (service?.price || 0) > 0;

  // On every step transition, scroll the window back to the top so patients
  // never miss content above the fold (esp. the "Special offer" banner on
  // the Payment step on mobile).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [step]);

  // Step list adapts: 0:Service+Loc, 1:Time, 2:Details, 3:(Payment if paid | Done if free), 4:Done (paid only)
  const stepsRender = isPaid
    ? [
        <StepServiceLocation key="s0" />,
        <StepTime key="s1" />,
        <StepDetails key="s2" />,
        <StepPayment key="s3" />,
        <StepConfirmFree key="s4" />, // never actually reached when paid (Stripe redirects)
      ]
    : [
        <StepServiceLocation key="s0" />,
        <StepTime key="s1" />,
        <StepDetails key="s2" />,
        <StepConfirmFree key="s3" />,
      ];

  return (
    <main className="max-w-4xl mx-auto" data-testid="wizard-main">
      <Stepper currentStep={step} isPaid={isPaid} />
      <div className="ws-card p-5 sm:p-8 lg:p-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {stepsRender[step]}
          </motion.div>
        </AnimatePresence>
      </div>
    </main>
  );
}

function HeaderWithSummary() {
  // Summary intentionally removed — the wizard steps already show this info.
  return <SiteHeader />;
}

export default function BookingPage() {
  return (
    <BookingProvider>
      <Elements stripe={stripePromise} options={{ locale: "en" }}>
        <div className="min-h-screen bg-[#FAFAFA]">
          <HeaderWithSummary />
          <div className="px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <WizardBody />
          </div>
          <footer className="border-t border-[#E5E5E5] py-8 mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-[#5C5C5C]">
              © {brandName()}
            </div>
          </footer>
        </div>
      </Elements>
    </BookingProvider>
  );
}
